import { useContext, useEffect, useMemo, useState } from 'react';
import { AdminContext } from '../../contexts/contexAdmin';
import { StatusUser, UserAdmin } from '../../styles/admin';
import { RiUserAddFill } from 'react-icons/ri';
import { FaUserEdit } from 'react-icons/fa';
import { ImBin } from 'react-icons/im';
import { FiFilter, FiSearch } from 'react-icons/fi';
import { HomeContext } from '../../contexts/contexHome';

const FILTERS = [
  { id: 'todos', label: 'Todos' },
  { id: 'online', label: 'Online' },
  { id: 'usuarios', label: 'Usuários' },
  { id: 'gtw', label: 'GTW' },
  { id: 'tanques', label: 'Tanques' },
];

const getUserCategory = (user) => {
  if (user.gateway !== null && user.gateway !== undefined) return 'gtw';
  if (user.tanque !== null && user.tanque !== undefined) return 'tanques';
  return 'usuarios';
};

const getCategoryLabel = (category) => {
  if (category === 'gtw') return 'GTW';
  if (category === 'tanques') return 'Tanque';
  return 'Usuário';
};

export const AdminUser = () => {
  const [activeFilter, setActiveFilter] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');

  const {
    usersSave,
    modalCreatingUser,
    setModalCreatingUser,
    setSaveUserId,
    modalUserDeletConfirmation,
    setModalUserDeletConfirmation,
    modaUpdatePassword,
    setModalUpdatePassword,
    modalEditUser,
    setModalEditUser,
    uservalidation,
  } = useContext(AdminContext);

  const { socket } = useContext(HomeContext);

  useEffect(() => {
    if (!socket) return;

    const updateUsers = () => uservalidation();
    socket.on('user_updated', updateUsers);
    socket.on('changed_user', updateUsers);

    return () => {
      socket.off('user_updated', updateUsers);
      socket.off('changed_user', updateUsers);
    };
  }, [socket, uservalidation]);

  const filterCounts = useMemo(() => {
    return usersSave.reduce(
      (counts, user) => {
        counts.todos += 1;
        counts[getUserCategory(user)] += 1;
        if (user.online) counts.online += 1;
        return counts;
      },
      { todos: 0, online: 0, usuarios: 0, gtw: 0, tanques: 0 },
    );
  }, [usersSave]);

  const visibleUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase('pt-BR');

    return [...usersSave]
      .filter((user) => {
        const category = getUserCategory(user);
        const matchesFilter =
          activeFilter === 'todos' ||
          (activeFilter === 'online' ? Boolean(user.online) : category === activeFilter);

        if (!matchesFilter) return false;
        if (!normalizedSearch) return true;

        return [user.nome, user.usuario, user.email, user.matricula]
          .filter(Boolean)
          .some((value) => String(value).toLocaleLowerCase('pt-BR').includes(normalizedSearch));
      })
      .sort((firstUser, secondUser) => {
        const firstName = firstUser.nome || firstUser.usuario || '';
        const secondName = secondUser.nome || secondUser.usuario || '';
        return firstName.localeCompare(secondName, 'pt-BR', { sensitivity: 'base', numeric: true });
      });
  }, [activeFilter, searchTerm, usersSave]);

  return (
    <UserAdmin>
      <div className="Header">
        <h2>Usuários</h2>
        <button
          type="button"
          className="addIcon"
          onClick={() => {
            setModalCreatingUser(!modalCreatingUser);
          }}
        >
          <RiUserAddFill />
          <span>Novo usuário</span>
        </button>
      </div>

      <div className="userFilters">
        <div className="filterHeading">
          <FiFilter aria-hidden="true" />
          <span>Categorias</span>
        </div>
        <div className="filterTabs" role="tablist" aria-label="Filtrar usuários por categoria">
          {FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              role="tab"
              aria-selected={activeFilter === filter.id}
              className={activeFilter === filter.id ? 'activeFilter' : ''}
              onClick={() => setActiveFilter(filter.id)}
            >
              <span>{filter.label}</span>
              <strong>{filterCounts[filter.id]}</strong>
            </button>
          ))}
        </div>
        <label className="userSearch">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar por nome, usuário ou e-mail"
            aria-label="Buscar usuários"
          />
        </label>
      </div>

      <div className="filterResult">
        <span>
          {visibleUsers.length} {visibleUsers.length === 1 ? 'registro encontrado' : 'registros encontrados'}
        </span>
      </div>

      {visibleUsers.length === 0 ? (
        <div className="emptyUsers">
          <strong>Nenhum registro encontrado</strong>
          <span>Altere a categoria ou o texto da busca.</span>
        </div>
      ) : (
        <ul>
          {visibleUsers.map((user) => {
            const userInitial = (user.nome || user.usuario || '?').trim().charAt(0).toUpperCase();
            const userCategory = getUserCategory(user);

            return (
              <li key={user.id} className={user.online ? 'onlineUser' : 'offlineUser'}>
              <div className="userCardTop">
                <StatusUser
                  className="userStatus"
                  color={user.online ? 'rgba(41, 162, 11, 1)' : 'rgba(131, 131, 131, 0.6)'}
                >
                  <span className="online"></span>
                  <span>{user.online ? 'Online' : 'Offline'}</span>
                </StatusUser>

                <div className="userActions">
                  <button
                    type="button"
                    title="Editar usuário"
                    aria-label="Editar usuário"
                    onClick={() => {
                      setSaveUserId(user.id);
                      setModalEditUser(!modalEditUser);
                    }}
                  >
                    <FaUserEdit />
                  </button>
                  <button
                    type="button"
                    className="deleteUser"
                    title="Excluir usuário"
                    aria-label="Excluir usuário"
                    onClick={() => {
                      setSaveUserId(user.id);
                      setModalUserDeletConfirmation(!modalUserDeletConfirmation);
                    }}
                  >
                    <ImBin />
                  </button>
                </div>
              </div>

              <div className="userIdentity">
                <span className="userAvatar">{userInitial}</span>
                <div>
                  <h3>{user.nome || 'Nome não informado'}</h3>
                  <div className="userMeta">
                    <span>@{user.usuario || '--'}</span>
                    <span className={`categoryBadge ${userCategory}`}>
                      {getCategoryLabel(userCategory)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="userInfoGrid">
                <div className="emailInfo">
                  <span className="infoLabel">E-mail</span>
                  <span className="infoValue">{user.email || '--'}</span>
                </div>
                <div>
                  <span className="infoLabel">Conta</span>
                  <span className={`infoBadge ${user.is_active ? 'activeBadge' : 'inactiveBadge'}`}>
                    {user.is_active ? 'Ativa' : 'Inativa'}
                  </span>
                </div>
                <div>
                  <span className="infoLabel">Administrador</span>
                  <span className="infoValue">{user.role > 2 ? 'Sim' : 'Não'}</span>
                </div>
                <div>
                  <span className="infoLabel">Nível de acesso</span>
                  <span className="infoValue">{user.role}</span>
                </div>
              </div>

              <div className="userButtons">
                <button
                  type="button"
                  onClick={() => {
                    setSaveUserId(user.id);
                    setModalUpdatePassword(!modaUpdatePassword);
                  }}
                >
                  Alterar senha
                </button>
                <button
                  type="button"
                  className="primaryButton"
                  onClick={() => {
                    setSaveUserId(user.id);
                    setModalEditUser(!modalEditUser);
                  }}
                >
                  Editar usuário
                </button>
              </div>
              </li>
            );
          })}
        </ul>
      )}
    </UserAdmin>
  );
};
